import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Flag, Mail } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';

import { entriesApi, errorMessage, qk } from '@/api';
import { BandPill, QuantityLine } from '@/components/BandPill';
import {
  PageHeader,
  FilterBar,
  ErrorState,
  LoadingState,
  EmptyState,
} from '@/components/PageHeader';
import { SummaryTile } from '@/components/SummaryTile';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input, Select } from '@/components/ui/input';
import { Card, Table, Th } from '@/components/ui/table';
import { CheckboxChip } from '@/components/ui/toggle';
import { useToast } from '@/components/ui/toast';
import { useCompanyBasics } from '@/features/auth/session';
import { EmailReportDialog } from '@/features/entries/EmailReportDialog';
import { useJobsList } from '@/features/jobs/useJobs';
import { cn } from '@/lib/cn';
import { fmtClock, fmtDateLong, fmtDateWeekday, fmtTime, todayInTz } from '@/lib/dates';
import { formatQuantity, hrs, plural } from '@/lib/format';
import { sortRows, useTableSort } from '@/lib/sort';
import { useUrlState } from '@/lib/url-state';

import {
  deriveSummary,
  filterDashboard,
  filterOptions,
  jobQuantityTotals,
  shortJob,
  type TodayForeman,
  type TodayJob,
} from './derive';
import { useDashboardToday } from './useDashboard';

const DEFAULTS = { date: '', foreman: '', client: '', red: '', missing: '', sort: '' };

/** W2 — every active job for the chosen date with who submitted, when, and each cost code vs plan. */
export function DashboardPage() {
  const { tz, cutoffTime } = useCompanyBasics();
  const today = todayInTz(tz);
  const [state, setState] = useUrlState(DEFAULTS);
  const date = state.date || today;
  const query = useDashboardToday(date);
  const sort = useTableSort(state.sort, (v) => setState({ sort: v }));
  const toast = useToast();
  const [emailFor, setEmailFor] = useState<{
    entryId: string;
    jobId: string;
    label: string;
  } | null>(null);
  const [emailAll, setEmailAll] = useState(false);

  const filters = useMemo(
    () => ({
      foreman: state.foreman,
      client: state.client,
      red: state.red === '1',
      missing: state.missing === '1',
    }),
    [state],
  );
  const allJobs = query.data?.jobs ?? [];
  const filtered = useMemo(() => filterDashboard(allJobs, filters), [allJobs, filters]);
  const summary = useMemo(() => deriveSummary(filtered), [filtered]);
  const options = useMemo(() => filterOptions(allJobs), [allJobs]);
  const isFiltered = !!(state.foreman || state.client || state.red || state.missing);

  const sortedJobs = useMemo(
    () =>
      sortRows(filtered, sort.column, sort.dir, {
        job: (j) => j.jobName,
        foreman: (j) => j.foremen[0]?.foremanName ?? '',
        submitted: (j) => j.foremen[0]?.submittedAt ?? '',
        labor: (j) => j.totals.laborHours,
        machine: (j) => j.totals.machineHours,
        photos: (j) => j.totals.photoCount,
      }),
    [filtered, sort.column, sort.dir],
  );

  const pastCutoff = query.data?.pastCutoff ?? false;
  const isToday = date === today;

  return (
    <>
      <PageHeader
        title={isToday ? 'Today' : fmtDateWeekday(date)}
        sub={`${fmtDateLong(date)} · cutoff ${fmtClock(cutoffTime)} · refreshes every 60 s`}
        actions={
          <>
            <Input
              type="date"
              aria-label="Date"
              value={date}
              max={today}
              className="w-auto font-semibold"
              onChange={(e) => setState({ date: e.target.value === today ? '' : e.target.value })}
            />
            <Button
              onClick={() => setEmailAll(true)}
              disabled={!query.data || summary.submitted === 0}
            >
              <Mail /> Email all reports
            </Button>
          </>
        }
      />

      <div className="sumstrip">
        <SummaryTile
          label="Days submitted"
          value={summary.submitted}
          unit={`of ${summary.foremanDays}`}
          sub={`across ${plural(summary.activeJobs, 'active job')}`}
        />
        <SummaryTile
          label={pastCutoff ? 'Missing after cutoff' : 'Not yet submitted'}
          value={pastCutoff ? summary.missing : summary.notYet}
          tone={pastCutoff && summary.missing > 0 ? 'bad' : 'default'}
          sub={
            pastCutoff
              ? summary.missingNames.length
                ? summary.missingNames.slice(0, 2).join(', ')
                : 'everyone is in'
              : summary.notYet > 0
                ? `flagged missing after the ${fmtClock(cutoffTime)} cutoff`
                : 'everyone is in'
          }
        />
        <SummaryTile
          label="Codes behind plan"
          value={summary.codesBehind}
          tone={summary.codesRed > 0 ? 'warn' : 'default'}
          sub={`${summary.codesRed} red · ${summary.codesAmber} amber`}
        />
        <SummaryTile
          label={isToday ? 'Labor today' : 'Labor'}
          value={hrs(summary.laborHours)}
          unit="h"
          sub={`${hrs(summary.machineHours)} machine h`}
        />
        <SummaryTile
          label={isToday ? 'Photos today' : 'Photos'}
          value={summary.photoCount}
          sub={summary.photosPending ? `${summary.photosPending} still uploading` : 'all uploaded'}
        />
      </div>

      <FilterBar right={query.data ? `Updated ${fmtTime(query.data.generatedAt, tz)}` : null}>
        <Select
          aria-label="Foreman"
          value={state.foreman}
          onChange={(e) => setState({ foreman: e.target.value })}
          className="w-auto"
        >
          <option value="">Foreman · All</option>
          {options.foremen.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Client"
          value={state.client}
          onChange={(e) => setState({ client: e.target.value })}
          className="w-auto"
        >
          <option value="">Client · All</option>
          {options.clients.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
        <CheckboxChip
          label="Red only"
          checked={state.red === '1'}
          onChange={(v) => setState({ red: v ? '1' : '' })}
        />
        <CheckboxChip
          label="Missing only"
          checked={state.missing === '1'}
          onChange={(v) => setState({ missing: v ? '1' : '' })}
        />
        {isFiltered ? (
          <Button
            variant="link"
            onClick={() => setState({ foreman: '', client: '', red: '', missing: '' })}
          >
            Clear filters
          </Button>
        ) : null}
      </FilterBar>

      {query.isPending ? (
        <Card>
          <LoadingState />
        </Card>
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <Card>
          <Table className="dash">
            <thead>
              <tr>
                <Th sortKey="job" sort={sort}>
                  Job
                </Th>
                <Th sortKey="foreman" sort={sort}>
                  Foreman
                </Th>
                <Th sortKey="submitted" sort={sort}>
                  Submitted
                </Th>
                <Th>Quantities today · actual / plan</Th>
                <Th right sortKey="labor" sort={sort}>
                  Labor h
                </Th>
                <Th right sortKey="machine" sort={sort}>
                  Machine h
                </Th>
                <Th sortKey="photos" sort={sort}>
                  Photos
                </Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {sortedJobs.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <EmptyState>
                      {isFiltered
                        ? 'Nothing matches these filters.'
                        : 'No active jobs for this date.'}
                    </EmptyState>
                  </td>
                </tr>
              ) : null}
              {sortedJobs.map((job) => (
                <JobRows
                  key={job.jobId}
                  job={job}
                  tz={tz}
                  pastCutoff={pastCutoff}
                  cutoffLabel={fmtClock(cutoffTime)}
                  onEmail={(row) =>
                    row.entryId &&
                    setEmailFor({
                      entryId: row.entryId,
                      jobId: job.jobId,
                      label: `${shortJob(job.jobName)} · ${fmtDateWeekday(date)}`,
                    })
                  }
                  onRemind={(row) =>
                    toast.success(
                      `Reminder sent to ${row.foremanName.split(' ')[0]} for ${shortJob(job.jobName)}`,
                    )
                  }
                />
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      <p className="mt-3 flex flex-wrap items-center gap-2 text-xs text-ink3">
        Bands: <BandPill band="green" pct={null} label="≥ 95%" />{' '}
        <BandPill band="amber" pct={null} label="80–94%" />{' '}
        <BandPill band="red" pct={null} label="< 80%" /> of plan/day. Thresholds are set in
        Settings.
      </p>

      {emailFor ? (
        <EmailReportDialog
          open
          entryId={emailFor.entryId}
          jobId={emailFor.jobId}
          label={emailFor.label}
          onClose={() => setEmailFor(null)}
        />
      ) : null}
      {emailAll && query.data ? (
        <EmailAllDialog jobs={filtered} date={date} onClose={() => setEmailAll(false)} />
      ) : null}
    </>
  );
}

function JobRows({
  job,
  tz,
  pastCutoff,
  cutoffLabel,
  onEmail,
  onRemind,
}: {
  job: TodayJob;
  tz: string;
  pastCutoff: boolean;
  cutoffLabel: string;
  onEmail: (row: TodayForeman) => void;
  onRemind: (row: TodayForeman) => void;
}) {
  const multi = job.foremen.length > 1;
  const jobCell = (full: boolean) => (
    <td className="job">
      {full ? (
        <>
          <Link to={`/jobs/${job.jobId}`} className="font-semibold text-orange-ink hover:underline">
            {job.jobName}
          </Link>
          <span className="sub">{job.clientName ?? '—'}</span>
        </>
      ) : (
        <span className="text-xs text-ink3">{shortJob(job.jobName)}</span>
      )}
    </td>
  );
  return (
    <>
      {multi ? (
        <tr className="jobhead">
          <td colSpan={8}>
            <Link to={`/jobs/${job.jobId}`} className="text-orange-ink hover:underline">
              {job.jobName}
            </Link>{' '}
            <span className="text-xs font-normal text-ink3">
              {job.clientName} · {job.foremen.length} foremen
            </span>
          </td>
        </tr>
      ) : null}
      {job.foremen.map((row) => (
        <tr
          key={row.foremanId}
          className={cn(row.missing && pastCutoff && 'missing')}
          data-testid="dash-row"
        >
          {jobCell(!multi)}
          <td data-l="Foreman">{row.foremanName}</td>
          <td data-l="Submitted" className="num">
            {row.missing ? (
              <>
                <span
                  className={cn(
                    'inline-flex items-center gap-1 font-bold',
                    pastCutoff ? 'text-bad' : 'text-ink3',
                  )}
                >
                  {pastCutoff ? <Flag className="size-3.5" /> : null}
                  {pastCutoff ? 'Not yet' : '— not yet'}
                </span>
                <span className="sub">
                  {pastCutoff ? `past ${cutoffLabel} cutoff` : `cutoff ${cutoffLabel}`}
                </span>
              </>
            ) : (
              <>
                {fmtTime(row.submittedAt, tz)}
                {row.version && row.version > 1 ? (
                  <span className="sub">v{row.version} · office edit</span>
                ) : null}
                {row.entryStatus === 'needs_review' ? (
                  <span className="sub font-semibold text-bad">needs review</span>
                ) : null}
                {row.entryStatus === 'approved' ? <span className="sub">approved</span> : null}
              </>
            )}
          </td>
          <td className="qs">
            {row.missing ? (
              <span className="text-ink3">—</span>
            ) : (
              <div className="flex flex-col gap-0.5">
                {row.quantities.map((q) => (
                  <QuantityLine
                    key={q.costCodeId}
                    code={q.code}
                    actual={q.actual}
                    plan={q.plan}
                    unit={q.unit}
                    band={q.band}
                    pct={q.pct}
                    format={formatQuantity}
                  />
                ))}
              </div>
            )}
          </td>
          <td data-l="Labor h" className="r num">
            {row.missing ? '—' : hrs(row.laborHours)}
          </td>
          <td data-l="Machine h" className="r num">
            {row.missing ? '—' : hrs(row.machineHours)}
          </td>
          <td data-l="Photos" className="num">
            {row.missing ? (
              '—'
            ) : (
              <span
                title={
                  row.photosPending
                    ? `${row.photoCount} photos, ${row.photosPending} still uploading`
                    : `${row.photoCount} photos`
                }
              >
                {row.photoCount}
                {row.photoCount > 0 ? (
                  <span className="hoverthumbs" aria-hidden>
                    {Array.from({ length: Math.min(3, row.photoCount) }).map((_, i) => (
                      <i key={i} />
                    ))}
                  </span>
                ) : null}
              </span>
            )}
          </td>
          <td className="act">
            {row.missing ? (
              <Button size="sm" onClick={() => onRemind(row)}>
                Remind {row.foremanName.split(' ')[0]}
              </Button>
            ) : row.entryId ? (
              <>
                <Link to={`/entries/${row.entryId}`}>
                  <Button size="sm">Open</Button>
                </Link>
                <Button size="sm" onClick={() => onEmail(row)}>
                  <Mail /> Email
                </Button>
              </>
            ) : (
              // No entry yet and the cutoff has not passed: nothing to open, nothing to chase.
              <span className="sub">Not yet</span>
            )}
          </td>
        </tr>
      ))}
      {multi ? (
        <tr className="tot">
          <td colSpan={3} className="text-ink3">
            {shortJob(job.jobName)} total
          </td>
          <td className="num">{jobQuantityTotals(job, formatQuantity)}</td>
          <td className="r num">{hrs(job.totals.laborHours)}</td>
          <td className="r num">{hrs(job.totals.machineHours)}</td>
          <td className="num">{job.totals.photoCount}</td>
          <td />
        </tr>
      ) : null}
    </>
  );
}

/** "Email all reports": every submitted entry for the date goes to its job's client emails. */
function EmailAllDialog({
  jobs,
  date,
  onClose,
}: {
  jobs: TodayJob[];
  date: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const jobList = useJobsList({ status: 'active' });
  const rows = jobs.flatMap((j) =>
    j.foremen
      .filter((r) => r.entryId)
      .map((r) => ({
        jobId: j.jobId,
        jobName: j.jobName,
        entryId: r.entryId!,
        foreman: r.foremanName,
      })),
  );
  const recipientsFor = (jobId: string) =>
    jobList.data?.items.find((j) => j.id === jobId)?.clientEmails ?? [];
  const sendable = rows.filter((r) => recipientsFor(r.jobId).length > 0);

  const send = useMutation({
    mutationFn: async () => {
      for (const r of sendable) await entriesApi.emailReport(r.entryId, recipientsFor(r.jobId));
      return sendable.length;
    },
    onSuccess: (n) => {
      toast.success(`${plural(n, 'report')} queued for email`);
      void qc.invalidateQueries({ queryKey: qk.entries.all() });
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Email all reports"
      description={`Daily reports for ${fmtDateLong(date)} go to each job's client emails.`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => send.mutate()}
            loading={send.isPending}
            disabled={sendable.length === 0}
          >
            Send {sendable.length} {sendable.length === 1 ? 'report' : 'reports'}
          </Button>
        </>
      }
    >
      <ul className="flex flex-col gap-2 text-[13px]">
        {rows.map((r) => {
          const to = recipientsFor(r.jobId);
          return (
            <li
              key={r.entryId}
              className="flex flex-wrap justify-between gap-2 border-b border-line2 pb-2"
            >
              <span>
                <b>{shortJob(r.jobName)}</b> · {r.foreman}
              </span>
              <span className={cn('text-xs', to.length ? 'text-ink3' : 'font-semibold text-warn')}>
                {to.length ? to.join(', ') : 'no client emails on this job — skipped'}
              </span>
            </li>
          );
        })}
        {rows.length === 0 ? <li className="text-ink3">Nothing submitted yet.</li> : null}
      </ul>
    </Dialog>
  );
}
